import { __decorate } from '../../../../node_modules/tslib/tslib.es6.mjs.js';
import '../../../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x } from '../../../../node_modules/lit-html/lit-html.js';
import { LitElement as s } from '../../../../node_modules/lit-element/lit-element.js';
import { unsafeHTML as o } from '../../../../node_modules/lit-html/directives/unsafe-html.js';
import { property as n } from '../../../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import { state as t } from '../../../../node_modules/lit/node_modules/@lit/reactive-element/decorators/state.js';
import '../../../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { trackVideoStarted, trackYoutubeVideoStarted, trackYoutubeVideoEnded, trackYoutubeVideoPosition, trackVideoPosition, trackVideoEnded } from './metrics.external-script.js';
import { VideoTypes } from './types.js';
import css_248z from './video-player.scss.js';

// eslint-disable-next-line no-use-before-define
const onYtReady = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    // The mount div normally lives in this component's shadow root, but the
    // lightbox physically re-parents the player's .video-container into its
    // overlay (document scope) when opened — look there as a fallback. Shadow
    // trees are excluded from document.getElementById, so this only matches the
    // re-parented node.
    const playerElement = ((_a = ctx.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(`player_${ctx.videoId}`)) ||
        document.getElementById(`player_${ctx.videoId}`);
    if (!playerElement)
        return;
    const playerVars = {
        rel: '0',
        origin: window.location.origin,
        enablejsapi: '1',
    };
    // When the player is created in response to a play gesture (not a
    // consent-at-load init), bake autoplay into the embed URL so playback starts
    // on load rather than via a post-ready playVideo() call, which the browser's
    // autoplay policy is more likely to block. Never emit autoplay=0.
    if (ctx.playWhenReady)
        playerVars.autoplay = 1;
    ctx.player = new window.YT.Player(playerElement, {
        host: 'https://www.youtube-nocookie.com',
        videoId: ctx.videoId,
        playerVars,
        events: {
            onReady: ctx.onYouTubePlayerReady,
            onStateChange: ctx.onYouTubePlayerStateChange,
        },
    });
};
const loadYoutubeIframeAPIScript = () => {
    var _a;
    if (!window.YT && !window.isYTInitializing) {
        window.isYTInitializing = true;
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        (_a = firstScriptTag.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(tag, firstScriptTag);
    }
};
// DCCC-8359: loading the IFrame API script from www.youtube.com sets tracking
// cookies, so it must not happen on page load — only after the OneTrust group
// covering YouTube is consented to, or on an explicit user gesture (click-to-load).
const YOUTUBE_CONSENT_GROUP = 'C0004';
const hasYoutubeConsent = () => typeof window.OnetrustActiveGroups === 'string' &&
    window.OnetrustActiveGroups.includes(YOUTUBE_CONSENT_GROUP);
const youtubeConsentCallbacks = [];
let youtubeConsentListenerAttached = false;
const drainYoutubeConsentCallbacks = () => {
    if (!hasYoutubeConsent())
        return;
    while (youtubeConsentCallbacks.length) {
        youtubeConsentCallbacks.shift()();
    }
};
const whenYoutubeConsent = (callback) => {
    if (hasYoutubeConsent()) {
        callback();
        return;
    }
    youtubeConsentCallbacks.push(callback);
    if (!youtubeConsentListenerAttached) {
        youtubeConsentListenerAttached = true;
        window.addEventListener('OneTrustGroupsUpdated', drainYoutubeConsentCallbacks);
        document.addEventListener('OneTrustGroupsUpdated', drainYoutubeConsentCallbacks);
    }
};
// eslint-disable-next-line no-use-before-define
const requestYoutubePlayerInit = (ctx) => {
    var _a;
    if (ctx.player)
        return;
    if ((_a = window.YT) === null || _a === void 0 ? void 0 : _a.Player) {
        onYtReady(ctx);
        return;
    }
    if (!window.videoPlayerReadyCallbacks)
        window.videoPlayerReadyCallbacks = [];
    if (!window.videoPlayerReadyCallbacks.some(cb => cb.context === ctx)) {
        window.videoPlayerReadyCallbacks.push({
            func: onYtReady,
            context: ctx,
        });
    }
    if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
            window.videoPlayerReadyCallbacks.forEach(cb => {
                cb.func(cb.context);
            });
            window.videoPlayerReadyCallbacks.length = 0;
        };
    }
    loadYoutubeIframeAPIScript();
};
class VideoPlayer extends s {
    constructor() {
        super();
        this.usedInFullImageVideo = false;
        this.usedInInlineFullVideo = false;
        this.reduceAnimation = false;
        this.usedInInline = false;
        this.insideTextBlock = false;
        this.isVideoStarted = false;
        this.player = null;
        this.GET_IFRAME_PARAMS = () => ({
            YouTube: `?playsinline=1&modestbranding=1${this.usedInFullImageVideo ? '&autoplay=1&loop=1&mute=1&controls=0' : ''}`,
            Vimeo: `?background=${this.usedInFullImageVideo ? '1' : '0'}`,
            inInlineFull: '&autoplay=1',
        });
        this.videoStartedHandler = (e, id) => {
            if (this.isVideoStarted)
                return;
            this.isVideoStarted = true;
            trackVideoStarted(e, id);
        };
        this.onYouTubePlayerStateChange = (event) => {
            switch (event.data) {
                case window.YT.PlayerState.ENDED:
                    trackYoutubeVideoEnded(this.player.getVideoUrl(), this.player.getVideoData().title);
                    break;
                case window.YT.PlayerState.PLAYING:
                    if (!this.isVideoStarted) {
                        trackYoutubeVideoStarted(this.player.getVideoUrl(), this.player.getVideoData().title);
                        setInterval(() => this.player.trackPostionHanler(this.player.getVideoUrl(), this.player.getVideoData().title, this.player.getDuration(), this.player.getCurrentTime(), event.data === window.YT.PlayerState.PLAYING), 300);
                        this.isVideoStarted = true;
                    }
                    break;
            }
        };
        this.onYouTubePlayerReady = () => {
            this.player.trackPostionHanler = trackYoutubeVideoPosition();
            if (this.playWhenReady) {
                this.playWhenReady = false;
                this._ensurePlaying();
            }
        };
        this.playWhenReady = false;
        this.trackVideoPosition = trackVideoPosition();
        this.renderVideoBlock = () => {
            let iframeSrc = '';
            switch (this.type) {
                case VideoTypes.LOCAL:
                    return x `<div class="video-player-wrapper">${this.renderLocalVideo(this.usedInFullImageVideo)}</div>`;
                case VideoTypes.YOUTUBE:
                    iframeSrc = `https://www.youtube-nocookie.com/embed/${this.videoId + this.setIframeParams(VideoTypes.YOUTUBE)}`;
                    return this.usedInFullImageVideo
                        ? x `<iframe title="youtube player" .src="${iframeSrc}" allowfullscreen allow="autoplay"></iframe>`
                        : x `<div id="player_${this.videoId}"></div>`;
                case VideoTypes.VIMEO:
                    iframeSrc = `https://player.vimeo.com/video/${this.videoId + this.setIframeParams(VideoTypes.VIMEO)}`;
                    return x `<iframe title="vimeo player" .src="${iframeSrc}" allowfullscreen allow="autoplay"></iframe>`;
                default:
                    return null;
            }
        };
        this._query = '(max-width: 767px)';
        this._isMobile = false;
        this._isAutoplay = !!(this.autoplay && !this._isMobile);
    }
    _handleMediaQuery(event) {
        this._isMobile = event.detail.value;
        this.requestUpdate();
    }
    _triggerLocalVideo() {
        var _a;
        if (this._isAutoplay &&
            this.type === VideoTypes.LOCAL &&
            this.usedInFullImageVideo) {
            const video = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('video');
            video.muted = !!this.muted;
            video === null || video === void 0 ? void 0 : video.play();
        }
    }
    // Re-issue playVideo until the player actually starts. Opening a lightbox
    // re-parents (and reloads) the player iframe, so a single play command can
    // land mid-reload and be dropped; poll the state briefly until it plays.
    _ensurePlaying(attempt = 0) {
        var _a, _b, _c, _d;
        const state = (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getPlayerState) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (state === 1 || state === 3)
            return; // playing / buffering
        (_d = (_c = this.player) === null || _c === void 0 ? void 0 : _c.playVideo) === null || _d === void 0 ? void 0 : _d.call(_c);
        if (attempt < 8) {
            setTimeout(() => this._ensurePlaying(attempt + 1), 250);
        }
    }
    initPlayer() {
        if (this.type !== VideoTypes.YOUTUBE)
            return;
        requestYoutubePlayerInit(this);
    }
    initAndPlay() {
        var _a;
        if (this.type !== VideoTypes.YOUTUBE)
            return false;
        if ((_a = this.player) === null || _a === void 0 ? void 0 : _a.playVideo) {
            this._ensurePlaying();
            return true;
        }
        this.mountCookieFreeIframe(true);
        if (hasYoutubeConsent()) {
            this.playWhenReady = true;
            this.initPlayer();
        }
        else {
            // Playback needs no YouTube API — the nocookie embed plays cookie-free.
            // The API bootstrap (whose response sets .youtube.com cookies) attaches
            // only if/when consent is granted, and playWhenReady stays false so the
            // late attach never (re)starts playback.
            whenYoutubeConsent(() => this.initPlayer());
        }
        return true;
    }
    // Create the nocookie embed iframe synchronously inside the user's click so
    // the browser carries the gesture into the iframe load and permits autoplay.
    // Waiting for the IFrame API to create it (a network round-trip later) makes
    // the load non-gesture-initiated and autoplay gets blocked; instead the API
    // attaches to this existing iframe (enablejsapi + origin) if consent allows.
    mountCookieFreeIframe(withAutoplay = true) {
        var _a;
        const id = `player_${this.videoId}`;
        const mount = ((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(id)) || document.getElementById(id);
        if (!mount || mount.tagName === 'IFRAME')
            return;
        const iframe = document.createElement('iframe');
        iframe.id = id;
        iframe.title = this.videoname || 'youtube player';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('allowfullscreen', '');
        iframe.src = `https://www.youtube-nocookie.com/embed/${this.videoId}?rel=0&playsinline=1${withAutoplay ? '&autoplay=1' : ''}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
        mount.replaceWith(iframe);
    }
    firstUpdated() {
        var _a;
        const parentVideoBlock = this.parentElement;
        if ((_a = parentVideoBlock === null || parentVideoBlock === void 0 ? void 0 : parentVideoBlock.parentElement) === null || _a === void 0 ? void 0 : _a.classList.contains('rich-text')) {
            this.insideTextBlock = true;
        }
        if (this.type === VideoTypes.YOUTUBE && this.autoinit) {
            whenYoutubeConsent(() => this.initPlayer());
        }
    }
    updated() {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reduceAnimation = !!(!mediaQuery || mediaQuery.matches);
        this._triggerLocalVideo();
        this._isAutoplay = !!(this.autoplay && !this.reduceAnimation);
    }
    renderLocalVideo(isBackgroundVideo) {
        const videoEl = o(`<source src="${this.videoId}" 
      type="video/mp4" />
      <track 
        src="${this.defaultClosedCaption}" 
        kind="subtitles" 
        srclang="en" 
        label="English" 
        default>
      `);
        return isBackgroundVideo
            ? x `<video ?autoplay="${this._isAutoplay}" ?muted="${this.muted}" ?loop="${this.loop}" playsinline>${videoEl}</video>`
            : x `<video @timeupdate="${(e) => this.trackVideoPosition(e, this.videoname)}" @play="${(e) => this.videoStartedHandler(e, this.videoname)}" @ended="${(e) => trackVideoEnded(e, this.videoname)}" controls playsinline>${videoEl}</video>`;
    }
    setIframeParams(videoType) {
        if (!this._isAutoplay)
            return '';
        let params = this.GET_IFRAME_PARAMS()[videoType];
        if (this.usedInInlineFullVideo) {
            params += this.GET_IFRAME_PARAMS().inInlineFull;
        }
        if (videoType === VideoTypes.YOUTUBE) {
            params += `&playlist=${this.videoId}`;
        }
        return params;
    }
    render() {
        return x `<div class="video-container ${this.usedInFullImageVideo ? 'inFullImageVideo' : ''} ${this.usedInInlineFullVideo ? 'usedInInlineFullVideo' : ''} ${this.insideTextBlock ? 'insideTextBlock' : ''} ${this.usedInInline ? 'usedInInline' : ''}">${this.renderVideoBlock()}</div><lit-media-query .query="${this._query}" @changed="${this._handleMediaQuery}"></lit-media-query>`;
    }
}
VideoPlayer.styles = css_248z;
__decorate([
    n()
], VideoPlayer.prototype, "type", void 0);
__decorate([
    n()
], VideoPlayer.prototype, "videoId", void 0);
__decorate([
    n()
], VideoPlayer.prototype, "videoname", void 0);
__decorate([
    n({ type: String })
], VideoPlayer.prototype, "defaultClosedCaption", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "autoinit", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "autoplay", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "muted", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "loop", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "usedInFullImageVideo", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "usedInInlineFullVideo", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "reduceAnimation", void 0);
__decorate([
    n({ type: Boolean })
], VideoPlayer.prototype, "usedInInline", void 0);
__decorate([
    t()
], VideoPlayer.prototype, "_isAutoplay", void 0);
customElements.define('video-player', VideoPlayer);

export { VideoPlayer, YOUTUBE_CONSENT_GROUP, hasYoutubeConsent, loadYoutubeIframeAPIScript, onYtReady, requestYoutubePlayerInit, whenYoutubeConsent };
